import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";

/**
 * Pagination state scoped to a table key.
 */
export type PaginationState = {
  currentPage: number;
  itemsPerPage: number;
};

type PaginationStore = Map<string, PaginationState>;

interface AtomTablePaginationContextValue {
  getPagination: (key: string) => PaginationState | undefined;
  setPagination: (key: string, pagination: PaginationState) => void;
}

const AtomTablePaginationContext =
  createContext<AtomTablePaginationContextValue | null>(null);

/**
 * Provider for AtomTable pagination state persistence across navigation.
 * Place this at the root of your app to enable session-level pagination persistence.
 */
export function AtomTablePaginationProvider({
  children,
}: {
  children: ReactNode;
}) {
  // Use ref to avoid re-renders when pagination changes.
  const storeRef = useRef<PaginationStore>(new Map());

  const getPagination = useCallback(
    (key: string): PaginationState | undefined => {
      return storeRef.current.get(key);
    },
    [],
  );

  const setPagination = useCallback(
    (key: string, pagination: PaginationState): void => {
      storeRef.current.set(key, pagination);
    },
    [],
  );

  const value = useMemo(
    () => ({ getPagination, setPagination }),
    [getPagination, setPagination],
  );

  return (
    <AtomTablePaginationContext.Provider value={value}>
      {children}
    </AtomTablePaginationContext.Provider>
  );
}

/**
 * Hook to access the AtomTable pagination context.
 * Returns null if used outside of AtomTablePaginationProvider.
 */
export function useAtomTablePaginationContext(): AtomTablePaginationContextValue | null {
  return useContext(AtomTablePaginationContext);
}
